import type { CognitoSignUpFailureResult } from './WorkyCognitoAuthServiceInterfaces';
import { CognitoSignUpFailureType } from './WorkySignUpFailureInterfaces';

const USERNAME_ALREADY_EXISTS_SIGN_UP_MESSAGE: string =
    'An account already exists for this email. Please sign in instead of registering again.';

const COGNITO_USERNAME_EXISTS_EXCEPTION_NAME: string = 'UsernameExistsException';

const COGNITO_USERNAME_EXISTS_MESSAGE_FRAGMENTS: readonly string[] = [
    'user already exists',
    'usernameexistsexception',
    'account with the given email already exists',
];

const readCognitoErrorName = (error: unknown): string | null => {
    if (typeof error !== 'object' || error === null) {
        return null;
    }

    const errorRecord: Record<string, unknown> = error as Record<string, unknown>;
    const errorName: unknown = errorRecord.name;
    if (typeof errorName === 'string' && errorName.trim().length > 0) {
        return errorName.trim();
    }

    return null;
};

const isUsernameAlreadyExistsCognitoError = (error: unknown): boolean => {
    const cognitoErrorName: string | null = readCognitoErrorName(error);
    if (cognitoErrorName === COGNITO_USERNAME_EXISTS_EXCEPTION_NAME) {
        return true;
    }

    const errorMessage: string =
        error instanceof Error ? error.message : String(error);
    const normalizedErrorMessage: string = errorMessage.trim().toLowerCase();

    return COGNITO_USERNAME_EXISTS_MESSAGE_FRAGMENTS.some((messageFragment: string): boolean => {
        return normalizedErrorMessage.includes(messageFragment);
    });
};

const buildGenericCognitoSignUpFailureResult = (
    errorMessage: string
): CognitoSignUpFailureResult => {
    return {
        failureType: CognitoSignUpFailureType.GENERIC,
        errorMessage,
    };
};

const buildCognitoSignUpFailureResult = (error: unknown): CognitoSignUpFailureResult => {
    if (isUsernameAlreadyExistsCognitoError(error)) {
        return {
            failureType: CognitoSignUpFailureType.USERNAME_ALREADY_EXISTS,
            errorMessage: USERNAME_ALREADY_EXISTS_SIGN_UP_MESSAGE,
        };
    }

    const errorMessage: string =
        error instanceof Error ? error.message : 'Sign-up failed. Please try again.';

    return {
        failureType: CognitoSignUpFailureType.GENERIC,
        errorMessage,
    };
};

const isUsernameAlreadyExistsSignUpFailure = (
    signUpFailure: CognitoSignUpFailureResult
): boolean => {
    return signUpFailure.failureType === CognitoSignUpFailureType.USERNAME_ALREADY_EXISTS;
};

export {
    USERNAME_ALREADY_EXISTS_SIGN_UP_MESSAGE,
    buildCognitoSignUpFailureResult,
    buildGenericCognitoSignUpFailureResult,
    isUsernameAlreadyExistsSignUpFailure,
};
